import {
  PrimaryRegularButton,
  SecondaryRegularButton
} from 'components/ReusableComponents/Button/RegularButtons'
import Dialog from 'components/ReusableComponents/Dialog'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import { useEmailSubscriptionGate } from 'components/ReusableComponents/UnlockServiceDialog'
import { SequencePlusIcon } from 'features/CustomCampaigns/EditCampaign/CampaignFlowIcons'
import { applyInviteEmailDesignToCampaign } from 'features/EmailEditor/applyInviteEmailDesignToCampaign'
import InviteEmailDesignTemplatePreview from 'features/InviteEmailDesign/InviteEmailDesignTemplatePreview'
import useAvailableImageUrl from 'hooks/useAvailableImageUrl'
import useBrand from 'hooks/useBrand'
import { useEffect, useState } from 'react'
import { useToasts } from 'react-toast-notifications'
import inviteEmailDesignApi from 'services/api/inviteEmailDesign'

import styles from './SequenceTemplatePickerModal.module.scss'

const DEFAULT_TEMPLATE_DESCRIPTION =
  'Clean and modern layout with a focused call to action.'

function SequenceTemplatePickerModal({
  campaignId,
  campaignName,
  stepTitle,
  initialTemplateId,
  onClose,
  onApplied,
  onPersistDraft
}) {
  const { promptEmailSubscriptionUpgrade } = useEmailSubscriptionGate()
  const { logoUrl: brandLogoCandidateUrl } = useBrand()
  const brandLogoUrl = useAvailableImageUrl(brandLogoCandidateUrl)
  const { addToast } = useToasts()

  const [templates, setTemplates] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [selectedTemplateId, setSelectedTemplateId] = useState(initialTemplateId ?? null)
  const [isApplying, setIsApplying] = useState(false)
  const [isCreatingTemplate, setIsCreatingTemplate] = useState(false)

  useEffect(() => {
    let isMounted = true

    setIsLoading(true)
    setLoadError(false)

    inviteEmailDesignApi
      .listTemplates()
      .then(response => {
        if (!isMounted) return

        setTemplates(response?.templates || [])
      })
      .catch(() => {
        if (!isMounted) return

        setTemplates([])
        setLoadError(true)
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false)
        }
      })

    return () => {
      isMounted = false
    }
  }, [])

  useEffect(() => {
    function refreshTemplatesOnFocus() {
      inviteEmailDesignApi
        .listTemplates()
        .then(response => setTemplates(response?.templates || []))
        .catch(() => {})
    }

    window.addEventListener('focus', refreshTemplatesOnFocus)
    return () => window.removeEventListener('focus', refreshTemplatesOnFocus)
  }, [])

  const selectedTemplate =
    templates.find(template => template.id === selectedTemplateId) || null

  function handleApply() {
    if (!selectedTemplate || isApplying) {
      return
    }

    if (promptEmailSubscriptionUpgrade()) {
      return
    }

    setIsApplying(true)

    applyInviteEmailDesignToCampaign({
      campaignId,
      templateId: selectedTemplate.id,
      brandLogoUrl
    })
      .then(() => {
        onApplied?.({
          templateId: selectedTemplate.id,
          templateName: selectedTemplate.name,
          templateDescription:
            selectedTemplate.layoutLabel || DEFAULT_TEMPLATE_DESCRIPTION
        })
      })
      .catch(() => {
        addToast(`Failed to apply "${selectedTemplate.name}".`, { appearance: 'error' })
      })
      .finally(() => {
        setIsApplying(false)
      })
  }

  async function handleCreateTemplate() {
    if (isCreatingTemplate) {
      return
    }

    const templateStudioWindow = window.open('', '_blank')
    if (templateStudioWindow) {
      templateStudioWindow.opener = null
    }

    setIsCreatingTemplate(true)

    try {
      await onPersistDraft?.()

      if (templateStudioWindow) {
        templateStudioWindow.location.href = '/marketing-assets/email-templates'
      } else {
        window.open('/marketing-assets/email-templates', '_blank', 'noopener,noreferrer')
      }
    } catch {
      templateStudioWindow?.close()
      addToast('Save the campaign draft before creating a new template.', {
        appearance: 'error'
      })
    } finally {
      setIsCreatingTemplate(false)
    }
  }

  return (
    <Dialog verticalPadding={24}>
      <div className={styles.modal}>
        <header className={styles.header}>
          <div className={styles.headerCopy}>
            <h2 className={styles.title}>Choose an email template - {stepTitle}</h2>
            <p className={styles.intro}>
              Select a template to use in your campaign. You can create a new one or edit
              an existing template in Template Studio.
            </p>
          </div>
          <span className={styles.campaignName}>{campaignName}</span>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close template picker"
          >
            ×
          </button>
        </header>

        <div className={styles.body}>
          <div className={styles.gridPane}>
            {isLoading ? (
              <LoadingIndicator />
            ) : loadError ? (
              <p className={styles.stateMessage} role="alert">
                Email templates could not be loaded. Check your connection and try again.
              </p>
            ) : (
              <div className={styles.grid}>
                <button
                  type="button"
                  className={styles.createCard}
                  onClick={handleCreateTemplate}
                  disabled={isCreatingTemplate}
                >
                  <span className={styles.createIcon} aria-hidden="true">
                    <SequencePlusIcon />
                  </span>
                  <strong className={styles.createTitle}>
                    {isCreatingTemplate ? 'Saving campaign…' : 'Create new template'}
                  </strong>
                  <span className={styles.createDescription}>
                    Start from scratch and create a new template
                  </span>
                </button>
                {templates.map(template => {
                  const isSelected = template.id === selectedTemplateId

                  return (
                    <button
                      key={template.id}
                      type="button"
                      className={
                        isSelected ? styles.templateCardSelected : styles.templateCard
                      }
                      onClick={() => setSelectedTemplateId(template.id)}
                    >
                      <span className={styles.templateThumb}>
                        <InviteEmailDesignTemplatePreview
                          templateId={template.id}
                          variant="card"
                          fitToContainer
                          enabled
                        />
                      </span>
                      <span className={styles.templateNameRow}>
                        <strong className={styles.templateName}>{template.name}</strong>
                        {template.isActive && (
                          <span className={styles.activeBadge}>Active</span>
                        )}
                      </span>
                      <span className={styles.templateDescription}>
                        {template.layoutLabel || DEFAULT_TEMPLATE_DESCRIPTION}
                      </span>
                      <span className={styles.templateStats}>
                        <span className={styles.templateStat}>
                          <strong>{template.campaignCount}</strong>
                          <span>Campaigns</span>
                        </span>
                        <span className={styles.templateStat}>
                          <strong>
                            <i className={styles.activeDot} aria-hidden="true" />
                            {template.activeCampaignCount}
                          </strong>
                          <span>Active</span>
                        </span>
                        <span className={styles.templateUpdated}>
                          <span>Updated</span>
                          <span
                            className={styles.templateAvatar}
                            title={template.updatedByName || 'Recently updated'}
                          >
                            {template.updatedByInitials || '—'}
                          </span>
                        </span>
                      </span>
                    </button>
                  )
                })}
              </div>
            )}
            {!isLoading && !loadError && templates.length === 0 && (
              <p className={styles.stateMessage}>
                No email templates yet. Create your first reusable layout to continue.
              </p>
            )}
          </div>

          <aside className={styles.previewPane}>
            {selectedTemplate ? (
              <InviteEmailDesignTemplatePreview
                templateId={selectedTemplate.id}
                variant="inline"
                scaleToWidth
                enabled
              />
            ) : (
              <div className={styles.previewEmpty}>
                <span className={styles.previewEmptyIcon} aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M8 1.667 14.667 5 8 8.333 1.333 5 8 1.667ZM1.333 11 8 14.333 14.667 11M1.333 8 8 11.333 14.667 8"
                      stroke="currentColor"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                <strong className={styles.previewEmptyTitle}>Select a template</strong>
                <span className={styles.previewEmptyDescription}>
                  A live preview appears here before you commit.
                </span>
              </div>
            )}
          </aside>
        </div>

        <footer className={styles.footer}>
          <SecondaryRegularButton onClick={onClose} disabled={isApplying}>
            Cancel
          </SecondaryRegularButton>
          <PrimaryRegularButton
            onClick={handleApply}
            disabled={!selectedTemplate || isApplying}
          >
            {isApplying ? 'Applying...' : 'Use template'}
          </PrimaryRegularButton>
        </footer>
      </div>
    </Dialog>
  )
}

export default SequenceTemplatePickerModal
